import css from '@styled-system/css';
import variant from '@styled-system/variant';
import { ThemeStyles } from '@honda-canada/design-system-react';

import { TrimCardVariant } from '../TrimCardGrid/TrimCard';

const themeStyles = new ThemeStyles();

themeStyles.addBaseStyles('Wrapper')(
  ({ theme }) =>
    css({
      display: 'flex',
      flexDirection: 'column',
      gap: theme.space[5],
      mb: 'default',
    }),
  variant({
    prop: 'variant',
    variants: {
      fourCardLayout: {
        gap: ['m', 'm', '20px'],
        mb: ['default', 'default', 'l'],
      },
    },
  }),
);

themeStyles.addBaseStyles('TitleWrapper')(
  css({
    display: 'flex',
    flexDirection: ['row', 'row', 'column'],
    ml: ['zero', 'zero', 'zero'],
  }),
  variant({
    prop: 'hasFourColumn',
    variants: {
      fourCardLayout: {
        ml: ['zero', 'zero', 'zero'],
      },
    },
  }),
);

themeStyles.addBaseStyles('BackButtonWrapper')(({ isDesktopOnly }) =>
  css({
    display: isDesktopOnly ? ['none', 'contents', 'contents'] : 'contents',
  }),
);

themeStyles.addBaseStyles('ModelCardsGridTitle')(
  css({
    pb: ['m', 'm', '40px'],
    fontWeight: '500',
  }),
  variant({
    prop: 'hasFourColumn',
    variants: {
      fourCardLayout: {
        ml: 'zero',
      },
    },
  }),
);

themeStyles.addBaseStyles('ImageWrapper')(
  css({
    marginTop: 'xxs',
    width: ['auto', '20%', '20%'],
    height: ['10px', 'auto', '100%'],
  }),
);

themeStyles.addBaseStyles('BadgeImage')(() =>
  css({
    objectFit: 'contain',
    objectPosition: ['left', 'center', 'center'],
    width: ['auto', '100%', '100%'],
  }),
);

themeStyles.addBaseStyles('TrimCardsGridTitle')(
  css({
    fontWeight: '500',
  }),
  ({ hasBadge }) =>
    variant({
      prop: 'type',
      variants: {
        inventory: {
          width: hasBadge ? ['60%', '60%', '70%'] : 'auto',
        },
      },
    }),
  ({ hasBadge }) =>
    css({
      order: hasBadge ? [1, 0, 0] : 0,
    }),
);

themeStyles.addBaseStyles('TitleContainer')(
  css({
    display: 'flex',
    justifyContent: 'space-between',
    flexDirection: ['column', 'row', 'row'],
    gap: 'xs',
    width: ['100%', '100%', 'auto'],
    alignItems: 'start',
    flexWrap: 'wrap',
    pl: 'zero',
    pt: ['zero', 'zero', '19px'],
    pb: ['zero', 'zero', '50px'],
  }),
  variant({
    prop: 'variant',
    variants: {
      [TrimCardVariant.Inventory]: {
        pb: ['m', 'm', 'default'],
        pt: ['0', '0', '0'],
      },
    },
  }),
  variant({
    prop: 'hasFourColumn',
    variants: {
      fourCardLayout: {
        px: ['zero', 'zero', 'zero'],
        pt: ['zero', 'zero', 'zero'],
        pb: '16px !important',
      },
    },
  }),
);

themeStyles.addBaseStyles('Title')(
  css({
    textTransform: 'uppercase',
    fontSizes: '18px',
    px: 'zero',
  }),
);

themeStyles.addBaseStyles('List')(
  ({ theme }) =>
    css({
      display: 'grid',
      gridTemplateColumns: ['repeat(1, 1fr)', 'repeat(2, 1fr)', 'repeat(2, 1fr)', 'repeat(3, 1fr)'],
      columnGap: theme.space.columnGapFull[0],
      rowGap: theme.space.columnGapFull[1],
    }),
  ({ theme }) =>
    variant({
      prop: 'variant',
      variants: {
        fourCardLayout: {
          gridTemplateColumns: ['repeat(1, 1fr)', 'repeat(2, 1fr)', 'repeat(2, 1fr)', 'repeat(4, 1fr)'],
          columnGap: theme.space.columnGapFull[1],
          rowGap: theme.space.columnGapFull[0],
        },
        compact: {
          gridTemplateColumns: ['repeat(1, 1fr)', 'repeat(2, 1fr)', 'repeat(2, 1fr)', 'repeat(3, 1fr)'],
          columnGap: theme.space.columnGapFull[1],
          rowGap: ['s', 's', 'default'],
        },
      },
    }),
);

themeStyles.addBaseStyles('StyledBaseCarousel')(({ theme, currentSlide, itemWidth, totalSlides }) => {
  const HALF_GAP = theme.space.s / 2;
  const NEGATIVE_MULTIPLIER = -1;
  const FIRST_SLIDE_PADDING = 20;

  let translateX = (currentSlide * parseInt(itemWidth, 10) - HALF_GAP) * NEGATIVE_MULTIPLIER;

  const isLastSlide = currentSlide === totalSlides - 1;

  if (totalSlides > 2 && isLastSlide) {
    const totalWidthExcludingLast = (totalSlides - 1) * (parseInt(itemWidth, 10) + HALF_GAP) - FIRST_SLIDE_PADDING;
    const rightSpace = window.innerWidth - totalWidthExcludingLast;
    translateX = rightSpace - parseInt(itemWidth, 10) + HALF_GAP;
  }

  if (totalSlides === 2) {
    const totalWidth2Slides = parseInt(itemWidth, 10) + HALF_GAP;
    translateX = window.innerWidth - totalWidth2Slides - parseInt(itemWidth, 10);
  }

  translateX = currentSlide === 0 ? FIRST_SLIDE_PADDING : translateX;

  return css({
    '& [aria-live="polite"]': {
      ...(itemWidth && {
        gap: 's',
        transform: `translateX(${translateX}px)`,
      }),
    },
    '& [aria-live="polite"] > div': {
      flex: itemWidth ? 1 : undefined,
      opacity: 1,
      visibility: 'visible',
      mt: 's',
      mb: theme.space.columnGapFull[1],
      display: 'flex',
      ...(!itemWidth && {
        display: 'flex',
        justifyContent: 'center',
      }),
    },
  });
});

themeStyles.addBaseStyles('StyledBasePaginator')(({ currentSlide, theme }) =>
  css({
    '&': {
      backgroundColor: theme.colors.white,
      boxShadow: '0 -2px 2px rgba(0,0,0,.1)',
      marginTop: 's',
    },
    '& button': {
      borderRadius: 0,
      backgroundColor: theme.colors.black,
    },
    '& svg path': {
      stroke: theme.colors.black,
    },
    [`& button:nth-child(${currentSlide + 1})`]: {
      backgroundColor: theme.colors.red,
    },
  }),
);

themeStyles.addBaseStyles('CardGridWrapper')(
  css({
    display: 'flex',
    flexDirection: 'column',
    mt: ['0', 'default', 'auto'],
    px: ['20px', 'm', 'zero'],
  }),
  variant({
    prop: 'type',
    variants: {
      trimCardGrid: {
        mt: ['m', 'm', '0'],
        px: ['m', 'm', 'zero'],
      },
    },
  }),
  variant({
    prop: 'hasFourColumn',
    variants: {
      fourCardLayout: {
        mt: ['default', 'default', 'xl'],
        px: ['zero', 'zero', 'zero'],
      },
    },
  }),
);

themeStyles.addBaseStyles('CompareLink')(
  css({
    display: 'inline-flex',
    alignItems: 'center',
    gap: 'xs',
    fontFamily: 'bold',
    color: 'blue',
    textDecoration: 'none',
    lineHeight: '24px',
    fontSize: '14px',
  }),
);

themeStyles.addBaseStyles('LinkContainer')(
  css({
    pb: 'default',
    ml: ['zero', 'm', 'zero'],
  }),
);

themeStyles.addBaseStyles('CTA')(
  css({
    mb: ['xs', 'xs', 'l'],
    mt: ['default', 'default', '0'],
    mx: 'auto',
  }),
);

export default themeStyles;
